import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { advanceAPI, employeeAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Plus, Loader2, Check, X, Banknote, FileDown, PauseCircle, PlayCircle, ChevronsUpDown, Pencil, Trash2, CalendarDays, History, User, CreditCard, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { formatIndianCurrency } from '../../lib/utils';
import * as XLSX from 'xlsx';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const SalaryAdvances = () => {
    const { user } = useAuth();
    const { dialogProps, confirm } = useConfirmation();
    const [advances, setAdvances] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [empDropdownOpen, setEmpDropdownOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editingAdvance, setEditingAdvance] = useState(null);
    const [editFormData, setEditFormData] = useState({
        amount: '',
        monthly_repayment_amount: '',
        repayment_start_month: '',
        repayment_start_year: '',
        reason: '',
    });
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [selectedAdvance, setSelectedAdvance] = useState(null);
    const [repayments, setRepayments] = useState([]);
    const [repaymentsLoading, setRepaymentsLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [detailTab, setDetailTab] = useState('repayments'); // 'repayments' | 'history'

    // Date defaults
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    const [formData, setFormData] = useState({
        employee_id: '',
        amount: '',
        monthly_repayment_amount: '',
        repayment_start_month: currentMonth + 1, // Default to next month
        repayment_start_year: currentYear,
        reason: '',
        month: currentMonth,
        year: currentYear,
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [advRes, empRes] = await Promise.all([
                advanceAPI.getAll(),
                employeeAPI.getEmployees({ page: 1, page_size: 1000, status: 'ACTIVE' }),
            ]);
            setAdvances(advRes.data);
            setEmployees(empRes.data.employees);
        } catch (error) {
            if (error.response?.status === 403) {
                setError(error.response.data.detail);
            } else {
                toast.error('Failed to fetch data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = () => {
        const data = advances.map(adv => ({
            'Employee': adv.employee_name,
            'Reason': adv.reason,
            'Amount': adv.amount,
            'Monthly Repayment': adv.monthly_repayment_amount || 0,
            'Total Paid': adv.total_repaid || 0,
            'Remaining': adv.amount - (adv.total_repaid || 0),
            'Start Month': `${adv.repayment_start_month}/${adv.repayment_start_year}`,
            'Status': adv.status,
            'Request Date': new Date(adv.request_date).toLocaleDateString()
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salary Advances");
        XLSX.writeFile(wb, `Salary_Advances_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            await advanceAPI.grant(formData.employee_id, {
                amount: parseFloat(formData.amount),
                monthly_repayment_amount: parseFloat(formData.monthly_repayment_amount),
                repayment_start_month: parseInt(formData.repayment_start_month),
                repayment_start_year: parseInt(formData.repayment_start_year),
                reason: formData.reason,
                month: parseInt(formData.month),
                year: parseInt(formData.year)
            });
            toast.success('Salary advance granted successfully');
            setIsCreateOpen(false);
            setFormData({
                employee_id: '',
                amount: '',
                monthly_repayment_amount: '',
                repayment_start_month: currentMonth + 1,
                repayment_start_year: currentYear,
                reason: '',
                month: currentMonth,
                year: currentYear,
            });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to grant advance');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleStatusUpdate = async (id, status, reason = null) => {
        const confirmed = await confirm({
            title: `Update Status to ${status}`,
            description: `Are you sure you want to mark this advance as ${status}?`,
            confirmText: status,
            variant: status === 'REJECTED' ? 'destructive' : 'default',
        });
        if (!confirmed) return;

        try {
            await advanceAPI.updateStatus(id, { status, rejection_reason: reason });
            toast.success(`Advance marked as ${status}`);
            fetchData();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleTogglePause = async (id, isPaused) => {
        const action = isPaused ? 'Resume' : 'Pause';
        const confirmed = await confirm({
            title: `${action} Deduction`,
            description: `Are you sure you want to ${action.toLowerCase()} salary deduction for this advance?`,
            confirmText: action,
        });
        if (!confirmed) return;

        try {
            await advanceAPI.updateStatus(id, { is_paused: !isPaused });
            toast.success(`Deduction ${isPaused ? 'resumed' : 'paused'} successfully`);
            fetchData();
        } catch (error) {
            toast.error('Failed to update pause status');
        }
    };

    const openEdit = (adv) => {
        setEditingAdvance(adv);
        setEditFormData({
            amount: adv.amount,
            monthly_repayment_amount: adv.monthly_repayment_amount,
            repayment_start_month: adv.repayment_start_month,
            repayment_start_year: adv.repayment_start_year,
            reason: adv.reason,
        });
        setIsEditOpen(true);
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        try {
            await advanceAPI.update(editingAdvance.id, {
                amount: parseFloat(editFormData.amount),
                monthly_repayment_amount: parseFloat(editFormData.monthly_repayment_amount),
                repayment_start_month: parseInt(editFormData.repayment_start_month),
                repayment_start_year: parseInt(editFormData.repayment_start_year),
                reason: editFormData.reason,
            });
            toast.success('Salary advance updated successfully');
            setIsEditOpen(false);
            setEditingAdvance(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update advance');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Salary Advance',
            description: 'Are you sure you want to delete this salary advance? This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;

        try {
            await advanceAPI.delete(id);
            toast.success('Salary advance deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete advance');
        }
    };
    
    const fetchRepayments = async (id) => {
        setRepaymentsLoading(true);
        try {
            const res = await advanceAPI.getRepayments(id);
            setRepayments(res.data);
        } catch (error) {
            toast.error('Failed to fetch repayment history');
        } finally {
            setRepaymentsLoading(false);
        }
    };

    const fetchHistory = async (id) => {
        setHistoryLoading(true);
        try {
            const res = await advanceAPI.getHistory(id);
            setHistory(res.data);
        } catch (error) {
            toast.error('Failed to fetch activity history');
        } finally {
            setHistoryLoading(false);
        }
    };

    const openDetail = (adv) => {
        setSelectedAdvance(adv);
        setIsDetailOpen(true);
        setDetailTab('repayments');
        fetchRepayments(adv.id);
        fetchHistory(adv.id);
    };

    const getMonthName = (m) => {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return months[m - 1] || m;
    };

    const formatHistoryEntry = (entry) => {
        const actionLabels = {
            CREATED: 'Advance created',
            EDITED: 'Advance edited',
            STATUS_CHANGED: 'Status changed',
            PAUSED: 'Deduction paused',
            RESUMED: 'Deduction resumed',
            REPAYMENT: 'Repayment deducted',
            REPAYMENT_REVERTED: 'Repayment reverted',
            CLOSED_BY_FNF: 'Closed by F&F settlement',
            DELETED: 'Advance deleted',
        };

        let details = actionLabels[entry.action] || entry.action;

        if (entry.action === 'STATUS_CHANGED' && entry.old_values && entry.new_values) {
            details += `: ${entry.old_values.status} → ${entry.new_values.status}`;
        }

        if (entry.action === 'EDITED' && entry.old_values && entry.new_values) {
            const changed = Object.keys(entry.new_values).map(k => {
                const label = k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                return `${label}: ${entry.old_values[k]} → ${entry.new_values[k]}`;
            }).join(', ');
            details += ` (${changed})`;
        }

        if (entry.amount && entry.action !== 'EDITED' && entry.action !== 'STATUS_CHANGED') {
            details += ` — ${formatIndianCurrency(entry.amount)}`;
        }

        if (entry.note) {
            details += ` · ${entry.note}`;
        }

        return details;
    };

    const getStatusBadge = (status) => {
        const styles = {
            PENDING: 'bg-yellow-100 text-yellow-800',
            APPROVED: 'bg-blue-100 text-blue-800',
            REJECTED: 'bg-red-100 text-red-800',
            PAID: 'bg-green-100 text-green-800',
            RECOVERED: 'bg-gray-100 text-gray-800',
        };
        return (
            <div className="flex gap-2 items-center">
                <Badge className={styles[status]}>{status}</Badge>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (error) {
        return <ModuleDisabled moduleName="salary_advances" />;
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Salary Advances</h1>
                    <p className="text-muted-foreground text-sm sm:text-base">Manage employee salary advance requests</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={handleDownloadExcel}>
                        <FileDown className="mr-2 h-4 w-4" />
                        Export Excel
                    </Button>
                    <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="mr-2 h-4 w-4" />
                                Grant Advance
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>Grant Salary Advance</DialogTitle>
                                <DialogDescription>Record a new salary advance for an employee</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleCreate}>
                                <div className="grid gap-4 py-4">
                                    <div className="space-y-2">
                                        <Label>Employee *</Label>
                                        <Popover open={empDropdownOpen} onOpenChange={setEmpDropdownOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    className="w-full justify-between font-normal"
                                                >
                                                    {formData.employee_id
                                                        ? (() => { const e = employees.find(e => e.id === formData.employee_id); return e ? `${e.first_name} ${e.last_name} (${e.employee_code})` : 'Select employee'; })()
                                                        : 'Select employee'}
                                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-full p-0" style={{ width: 'var(--radix-popover-trigger-width)' }}>
                                                <Command>
                                                    <CommandInput placeholder="Search employee..." />
                                                    <CommandList>
                                                        <CommandEmpty>No employee found.</CommandEmpty>
                                                        <CommandGroup>
                                                            {employees.map((emp) => (
                                                                <CommandItem
                                                                    key={emp.id}
                                                                    value={`${emp.first_name} ${emp.last_name} ${emp.employee_code}`}
                                                                    onSelect={() => {
                                                                        setFormData({ ...formData, employee_id: emp.id });
                                                                        setEmpDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    <Check className={`mr-2 h-4 w-4 ${formData.employee_id === emp.id ? 'opacity-100' : 'opacity-0'}`} />
                                                                    {emp.first_name} {emp.last_name} ({emp.employee_code})
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Amount *</Label>
                                        <Input
                                            type="number"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Monthly Repayment *</Label>
                                            <Input
                                                type="number"
                                                value={formData.monthly_repayment_amount}
                                                onChange={(e) => setFormData({ ...formData, monthly_repayment_amount: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Repayment Start (M/Y) *</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    placeholder="M"
                                                    value={formData.repayment_start_month}
                                                    onChange={(e) => setFormData({ ...formData, repayment_start_month: e.target.value })}
                                                    required
                                                    min="1" max="12"
                                                />
                                                <Input
                                                    type="number"
                                                    placeholder="Y"
                                                    value={formData.repayment_start_year}
                                                    onChange={(e) => setFormData({ ...formData, repayment_start_year: e.target.value })}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Reason</Label>
                                        <Input
                                            value={formData.reason}
                                            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                            placeholder="Reason for advance"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                                    <Button type="submit" disabled={createLoading}>
                                        {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Grant
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Advance Requests</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Monthly Pay</TableHead>
                                <TableHead>Paid</TableHead>
                                <TableHead>Remaining</TableHead>
                                <TableHead>Start Date</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {advances.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                        No salary advances found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                advances.map((adv) => (
                                    <TableRow key={adv.id}>
                                        <TableCell className="font-medium">
                                            <button 
                                                onClick={() => openDetail(adv)}
                                                className="text-left hover:text-primary transition-colors group"
                                            >
                                                <div className="font-bold border-b border-transparent group-hover:border-primary inline-block">
                                                    {adv.employee_name}
                                                </div>
                                                <div className="text-xs text-muted-foreground font-normal">{adv.reason}</div>
                                            </button>
                                        </TableCell>
                                        <TableCell>{formatIndianCurrency(adv.amount)}</TableCell>
                                        <TableCell>{formatIndianCurrency(adv.monthly_repayment_amount)}</TableCell>
                                        <TableCell className="text-green-600">{formatIndianCurrency(adv.total_repaid)}</TableCell>
                                        <TableCell className="font-medium">{formatIndianCurrency(adv.amount - (adv.total_repaid || 0))}</TableCell>
                                        <TableCell>{adv.repayment_start_month}/{adv.repayment_start_year}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {getStatusBadge(adv.status)}
                                                {adv.is_paused && (
                                                    <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50">PAUSED</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {(adv.status === 'PAID' || adv.status === 'APPROVED') && !adv.is_fully_repaid && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleTogglePause(adv.id, adv.is_paused)}
                                                        className={adv.is_paused ? "text-green-600 hover:text-green-700 hover:bg-green-50" : "text-orange-600 hover:text-orange-700 hover:bg-orange-50"}
                                                        title={adv.is_paused ? "Resume Deduction" : "Pause Deduction"}
                                                    >
                                                        {adv.is_paused ? <PlayCircle className="h-4 w-4 mr-1" /> : <PauseCircle className="h-4 w-4 mr-1" />}
                                                        {adv.is_paused ? "Resume" : "Pause"}
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEdit(adv)}
                                                    title="Edit"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(adv.id)}
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Salary Advance</DialogTitle>
                        <DialogDescription>Update advance details for {editingAdvance?.employee_name}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEdit}>
                        <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                                <Label>Amount *</Label>
                                <Input
                                    type="number"
                                    value={editFormData.amount}
                                    onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Monthly Repayment *</Label>
                                    <Input
                                        type="number"
                                        value={editFormData.monthly_repayment_amount}
                                        onChange={(e) => setEditFormData({ ...editFormData, monthly_repayment_amount: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Repayment Start (M/Y) *</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            placeholder="M"
                                            value={editFormData.repayment_start_month}
                                            onChange={(e) => setEditFormData({ ...editFormData, repayment_start_month: e.target.value })}
                                            required
                                            min="1" max="12"
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Y"
                                            value={editFormData.repayment_start_year}
                                            onChange={(e) => setEditFormData({ ...editFormData, repayment_start_year: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Reason</Label>
                                <Input
                                    value={editFormData.reason}
                                    onChange={(e) => setEditFormData({ ...editFormData, reason: e.target.value })}
                                    placeholder="Reason for advance"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={editLoading}>
                                {editLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save Changes
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <ConfirmationDialog {...dialogProps} />

            {/* Detail Modal */}
            <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl">
                            <Banknote className="h-5 w-5 text-primary" />
                            Salary Advance Details
                        </DialogTitle>
                        <DialogDescription>
                            Complete history and terms for {selectedAdvance?.employee_name}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedAdvance && (
                        <div className="space-y-8 py-4">
                            {/* Top Stats Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Card className="bg-slate-50 border-none">
                                    <CardContent className="pt-4 pb-4">
                                        <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider mb-1">
                                            <CreditCard className="h-3 w-3" />
                                            Total Advance
                                        </div>
                                        <div className="text-xl font-black">{formatIndianCurrency(selectedAdvance.amount)}</div>
                                        <div className="text-[10px] text-muted-foreground mt-1">Requested on {new Date(selectedAdvance.request_date).toLocaleDateString()}</div>
                                    </CardContent>
                                </Card>
                                <Card className="bg-green-50/50 border-none">
                                    <CardContent className="pt-4 pb-4">
                                        <div className="flex items-center gap-2 text-green-700 text-xs font-bold uppercase tracking-wider mb-1">
                                            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                            Total Recovered
                                        </div>
                                        <div className="text-xl font-black text-green-700">{formatIndianCurrency(selectedAdvance.total_repaid || 0)}</div>
                                        <div className="text-[10px] text-green-600 mt-1">{selectedAdvance.installments_paid} installments completed</div>
                                    </CardContent>
                                </Card>
                                <Card className="bg-primary/5 border-none shadow-sm">
                                    <CardContent className="pt-4 pb-4">
                                        <div className="flex items-center gap-2 text-primary text-xs font-bold uppercase tracking-wider mb-1">
                                            <History className="h-3 w-3" />
                                            Remaining Balance
                                        </div>
                                        <div className="text-xl font-black text-primary">{formatIndianCurrency(selectedAdvance.amount - (selectedAdvance.total_repaid || 0))}</div>
                                        <div className="text-[10px] text-primary/70 mt-1">Status: {selectedAdvance.status}</div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Terms Detail */}
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-slate-400" />
                                        Terms & Conditions
                                    </h3>
                                    <div className="rounded-xl border p-4 space-y-3 bg-white shadow-sm">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-muted-foreground">Monthly Installment</span>
                                            <span className="font-bold text-slate-700">{formatIndianCurrency(selectedAdvance.monthly_repayment_amount)}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-muted-foreground">Repayment Start</span>
                                            <span className="font-bold text-slate-700">{getMonthName(selectedAdvance.repayment_start_month)} {selectedAdvance.repayment_start_year}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-muted-foreground">Advance Pay Date</span>
                                            <span className="font-bold text-slate-700">{selectedAdvance.paid_date ? new Date(selectedAdvance.paid_date).toLocaleDateString() : '-'}</span>
                                        </div>
                                        <div className="pt-2 border-t mt-2">
                                            <span className="text-[10px] text-muted-foreground uppercase font-bold">Reason</span>
                                            <p className="text-sm text-slate-600 mt-1 italic">"{selectedAdvance.reason || 'No reason provided'}"</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex border-b">
                                        <button
                                            type="button"
                                            onClick={() => setDetailTab('repayments')}
                                            className={`px-4 py-2 text-sm font-bold ${detailTab === 'repayments' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
                                        >
                                            Deduction History
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDetailTab('history')}
                                            className={`px-4 py-2 text-sm font-bold ${detailTab === 'history' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
                                        >
                                            Activity History
                                        </button>
                                    </div>

                                    {detailTab === 'repayments' ? (
                                        <div className="rounded-xl border overflow-hidden bg-white shadow-sm">
                                            <Table>
                                                <TableHeader className="bg-slate-50">
                                                    <TableRow>
                                                        <TableHead className="text-[10px] font-black uppercase">Salary Month</TableHead>
                                                        <TableHead className="text-right text-[10px] font-black uppercase">Amount</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {repaymentsLoading ? (
                                                        <TableRow>
                                                            <TableCell colSpan={2} className="text-center py-10">
                                                                <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : repayments.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={2} className="text-center py-10 text-xs text-muted-foreground">
                                                                No deductions recorded yet
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        repayments.map((rep, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell className="font-medium text-sm">
                                                                    {getMonthName(rep.month)} {rep.year}
                                                                </TableCell>
                                                                <TableCell className="text-right font-bold text-red-600 text-sm">
                                                                    -{formatIndianCurrency(rep.amount)}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border overflow-hidden bg-white shadow-sm">
                                            <Table>
                                                <TableHeader className="bg-slate-50">
                                                    <TableRow>
                                                        <TableHead className="text-[10px] font-black uppercase">Date</TableHead>
                                                        <TableHead className="text-[10px] font-black uppercase">Activity</TableHead>
                                                        <TableHead className="text-right text-[10px] font-black uppercase">By</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {historyLoading ? (
                                                        <TableRow>
                                                            <TableCell colSpan={3} className="text-center py-10">
                                                                <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : history.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={3} className="text-center py-10 text-xs text-muted-foreground">
                                                                No activity history yet
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        history.map((h, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                                                    {new Date(h.created_at).toLocaleString()}
                                                                </TableCell>
                                                                <TableCell className="text-sm">
                                                                    {formatHistoryEntry(h)}
                                                                </TableCell>
                                                                <TableCell className="text-right text-xs text-muted-foreground">
                                                                    {h.performed_by_name || h.performed_by || 'System'}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDetailOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmationDialog {...dialogProps} />
        </div >
    );
};

export default SalaryAdvances;
